import { envConfig } from "Constant";
import { getAccessToken, refreshAccessToken } from "providers/auth-tokens";

interface CoachFetchOptions {
  method?: "GET" | "POST";
  // JSON-serialized when present; omitted entirely (no body, unchanged
  // behavior) when absent.
  body?: unknown;
}

export class CoachAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CoachAuthError";
  }
}

function coachHeaders(token: string | null | undefined): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    "X-Platform-Env": envConfig.PLATFORM_ENV ?? "",
    "Content-Type": "application/json",
  };
}

// Coach answers 204 with no body when a write leaves no record behind, and response.json() cannot parse that.
function coachBody<T>(response: Response): Promise<T> {
  return response.status === 204 ? Promise.resolve(null as T) : response.json();
}

export async function coachFetch<T>(
  path: string,
  { method = "GET", body }: CoachFetchOptions = {}
): Promise<T> {
  const url = `${envConfig.COACH_URL}${path}`;
  const requestInit = (token: string | null | undefined): RequestInit => ({
    method,
    headers: coachHeaders(token),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // Read the live token per request (mirrors Apollo's authLink), not a stale capture.
  const response = await fetch(url, requestInit(getAccessToken()));

  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      throw new CoachAuthError(`Coach ${path} unauthorized — session expired`);
    }
    const retry = await fetch(url, requestInit(refreshed));
    if (retry.status === 401) {
      throw new CoachAuthError(`Coach ${path} unauthorized — session expired`);
    }
    if (!retry.ok) {
      throw new Error(`Coach ${path} failed with status ${retry.status}`);
    }
    return coachBody<T>(retry);
  }

  if (!response.ok) {
    throw new Error(`Coach ${path} failed with status ${response.status}`);
  }

  return coachBody<T>(response);
}

// Tells Coach the learner has Ori's chat on screen; no body, Coach scopes it.
export async function reportChatViewed(): Promise<void> {
  await coachFetch<null>("/api/chat/viewed/", { method: "POST" });
}

/** One of a returning learner's starters, as Coach serves them. */
export interface ChatStarter {
  id: string;
  label: string;
  value: string;
  /** "new" is the "Try something new" starter. */
  kind: "known" | "new";
}

/** `GET /api/chat/starters/`: with the flag off, nothing to show, or any
 *  failure, Coach answers the empty set rather than an error. */
export interface ChatStartersResponse {
  /** Ori's line above the starters, null when none applies. */
  opening_line: string | null;
  /** Names the line on the chat message sent under it; null with no line. */
  opening_line_id: string | null;
  starters: ChatStarter[];
  /** Chat leads with these now: the start of a new conversation, never one
   *  under way, and not within two hours of a tap. */
  show_in_chat: boolean;
}

// Coach counts every read as a showing, so only a chat that can lead with
// them reads them.
export async function getChatStarters(): Promise<ChatStartersResponse> {
  return coachFetch<ChatStartersResponse>("/api/chat/starters/?screen=chat");
}

// Takes the starter off every screen; false (nothing recorded) once it is no
// longer live.
export async function tapChatStarter(
  id: string
): Promise<{ success: boolean }> {
  return coachFetch<{ success: boolean }>(
    `/api/chat/starters/${encodeURIComponent(id)}/tap/`,
    { method: "POST" }
  );
}
